import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { isObject } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { LossPreventionService } from '../../../../../service/loss-prevention.service';
import { PanoptycAdminService } from '../../../../../service/panoptyc-admin.service';
import { LPLocation } from '../../../../../types';

@Component({
  selector: 'app-loss-prevention-admin-location-instructions',
  templateUrl: './location-instructions.component.html',
  styleUrl: './location-instructions.component.scss',
  standalone: false,
})
export class LossPreventionAdminLocationInstructionsComponent implements OnInit {
  private adminService = inject(PanoptycAdminService);
  private lpService = inject(LossPreventionService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  public instructions = '';
  public selectedLocationId: number | null = null;
  public isError = false;
  // ongoing mutation flag
  public isPending = false;
  // data loading flag
  public isLoading = false;
  public message = '';

  locationControl = new FormControl();
  locationOptions: Observable<LPLocation[]>;

  ngOnInit() {
    this.locationOptions = this.locationControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      tap((val) => {
        if (!isObject(val) || !('location_id' in val)) return;

        this.selectedLocationId = val.location_id as number;
        this.isError = false;
        this.isLoading = true;
        this.message = 'Loading instructions...';
        this.lpService.getLocationInstructions(this.selectedLocationId).subscribe(
          (res) => {
            this.instructions = res.instructions;
            this.isLoading = false;
            this.message = '';
          },
          (err) => {
            this.isError = true;
            this.isLoading = false;
            this.message = err.message || JSON.stringify(err);
          },
        );
      }),
      switchMap((val) => this._filter(val || '')),
    );

    this.selectPreviousLocation();
  }

  public onSubmit() {
    this.isError = false;
    this.message = '';

    if (!this.selectedLocationId) {
      this.isError = true;
      this.message = 'No location selected';
      return;
    }

    this.isPending = true;
    this.lpService.updateLocationInstructions(this.selectedLocationId, this.instructions).subscribe(
      () => {
        this.isError = false;
        this.isPending = false;
        this.message = 'Successfully updated location instructions';
      },
      (err) => {
        this.isError = true;
        this.isPending = false;
        this.message = err.message || JSON.stringify(err);
      },
    );
  }

  private _filter(value: LPLocation | string): Observable<LPLocation[]> {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value.location_name.toLowerCase();

    return this.adminService.getLocationsAutocomplete(filterValue).pipe(
      // map the item property of the github results as our return object
      map((results) => results),
      // catch errors
      catchError(() => {
        return of(null);
      }),
    );
  }

  public search(term: string, item: LPLocation) {
    return (
      item.branch_name.toLowerCase().includes(term.toLowerCase()) ||
      item.location_name.toLowerCase().includes(term.toLowerCase()) ||
      item.branch_id.toString().includes(term.toLowerCase()) ||
      item.location_id.toString().includes(term.toLowerCase())
    );
  }

  public changeLocation(location: LPLocation) {
    if (!location) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParamsHandling: 'replace',
        queryParams: {},
      });
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: { location_id: location.location_id, branch_id: location.branch_id },
    });
  }

  private selectPreviousLocation() {
    const query = this.route.snapshot.queryParams;
    const { location_id } = query;
    if (!location_id) {
      return;
    }

    this.adminService.getLocationsAutocomplete('').subscribe((d) => {
      this.locationControl.setValue(d.find((l) => l.location_id === +location_id));
    });
  }
}
